<template>
  <v-container fluid>
    <v-row align-content="space-around">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title>Event based Mqtt Commands</v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-form ref="form" v-model="valid">
              <v-radio-group
                row
                v-model="deviceType"
                label="Device Type : "
                :rules="rules"
              >
                <v-radio label="Ecotron" value="ecotron"></v-radio>
                <v-radio label="Ecofrost" value="ecofrost"></v-radio>
              </v-radio-group>
              <div v-if="deviceType">
                <v-textarea
                  outlined
                  :label="placeHolder.commandLabel"
                  :placeholder="placeHolder.command"
                  rounded
                  no-resize
                  :rules="rules"
                  v-model="command"
                  :rows="3"
                ></v-textarea>
                <v-textarea
                  outlined
                  :label="placeHolder.deviceIdLabel"
                  :placeholder="placeHolder.deviceIds"
                  auto-grow
                  :rules="rules"
                  v-model="listOfDeviceIds"
                  rounded
                ></v-textarea>
                <v-textarea
                  outlined
                  :label="placeHolder.expressionLabel"
                  placeholder=" "
                  rounded
                  no-resize
                  :rules="rules"
                  v-model="evaluationExpression"
                  :rows="2"
                  readonly
                  clearable
                ></v-textarea>

                <v-container fluid class="pa-0">
                  <v-row align-content="space-around" justify="space-around">
                    <v-col cols="12" sm="12" md="6">
                      <v-card outlined>
                        <v-card-title class="caption">
                          Parameter for variables {{ deviceType }}
                        </v-card-title>
                        <v-card-subtitle
                          class="caption font-italic font-weight-light mb-0"
                        >
                          <b>Note </b>: All the data from mqtt comes in form of
                          string so use double quotes while comparing
                        </v-card-subtitle>
                        <v-divider></v-divider>

                        <v-btn
                          color="primary"
                          outlined
                          text
                          class="ma-1"
                          v-for="m in measurements"
                          :key="m.value"
                          @click="appendMeasurement(m.value)"
                        >
                          {{ m.value }}
                        </v-btn>
                      </v-card>
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                      <v-card outlined>
                        <v-card-title class="caption">
                          Numbers, Math operators and Logical operators
                        </v-card-title>
                        <v-card-subtitle
                          class="caption font-italic font-weight-light mb-0"
                        >
                          <b>Note :</b> parseInt and parseFloat are functions,
                          so don't forget to add round paranthesis
                        </v-card-subtitle>
                        <v-divider></v-divider>

                        <v-btn
                          color="primary"
                          outlined
                          text
                          class="ma-1"
                          v-for="n in numbersAndOperators"
                          :key="n"
                          @click="appendNumberAndOperator(n)"
                        >
                          {{ n }}
                        </v-btn>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-container>
              </div>
            </v-form>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" @click="createEventBased" :loading="loading">
              <v-icon left>mdi-clock-outline</v-icon>
              Schedule Event Job
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-row align-content="space-around">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title>List of event based jobs</v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-card outlined>
              <v-data-table :headers="headers" :items="items">
                <template #[`item.deleteAction`]="{ item }">
                  <v-btn
                    fab
                    small
                    text
                    color="red"
                    :disabled="
                      user.email !== 'alok@ecozensolutions.com' &&
                      user.email !== 'samir@ecozensolutions.com' &&
                      user.email !== item.created_by
                    "
                  >
                    <v-icon small @click="deleteRecord(item)">
                      mdi-delete
                    </v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="refreshResult">
              <v-icon left>mdi-restore</v-icon>
              Refresh
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import ecotronTableColumns from '../mixins/ecotron-table-columns';
import ecofrostTableColumns from '../mixins/ecofrost-table-columns';
import axios from 'axios';
import moment from 'moment';

const schedulerApi = axios.create({
  baseURL: 'https://api.ecozen.ai/scheduler/v1'
});

export default {
  name: 'event-based-mqtt',
  mixins: [ecofrostTableColumns, ecotronTableColumns],
  data() {
    return {
      valid: true,
      deviceType: null,
      rules: [value => !!value || 'Required'],
      command: '',
      listOfDeviceIds: '',
      evaluationExpression: '',
      numbersAndOperators: [
        '0',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '.',
        '+',
        '-',
        '*',
        '/',
        '===',
        '!==',
        '>',
        '<',
        '>=',
        '<=',
        '%',
        '(',
        ')',
        '&&',
        '||',
        '"',
        'parseInt',
        'parseFloat'
      ],
      headers: [
        {
          text: 'Device Type',
          value: 'machine_type',
          sortable: false
        },
        {
          text: 'Mqtt Command',
          value: 'execution_command',
          sortable: false
        },
        {
          text: 'Device Ids',
          value: 'machine_ids',
          sortable: false
        },
        {
          text: 'Evaluation expression',
          value: 'evaluate_expression',
          sortable: false
        },

        {
          text: 'Execution Count',
          value: 'execution_count',
          sortable: false
        },
        {
          text: 'Created By',
          value: 'created_by',
          sortable: false
        },
        {
          text: 'Last updated on',
          value: 'updated_at',
          sortable: false
        },
        {
          text: 'Delete',
          value: 'deleteAction',
          sortable: false
        }
      ],
      items: [],
      loading: false
    };
  },
  computed: {
    measurements() {
      if (this.deviceType) {
        if (this.deviceType === 'ecotron') {
          return this.ecotronHeaders.filter(
            element => element.value !== 'createdAt'
          );
        } else if (this.deviceType === 'ecofrost') {
          return this.ecofrostHeaders.filter(
            element => element.value !== 'createdAt'
          );
        } else {
          return [];
        }
      } else {
        return [];
      }
    },
    placeHolder() {
      if (this.deviceType) {
        if (this.deviceType === 'ecotron') {
          return {
            commandLabel: 'Ecotron Mqtt Command',
            command: 'Command Format #COMMAND1&#COMMAND2&',
            deviceIdLabel: 'List of ecotron devices',
            deviceIds:
              'Comma Seperated Serial Numbers Eg: EZMCI19120358,EZMCI19120359,EZMCI19120360',
            expressionLabel: 'Ecotron evaluation expression'
          };
        } else if (this.deviceType === 'ecofrost') {
          return {
            commandLabel: 'Ecofrost Mqtt Command',
            command:
              'Command Format should be parameter1:value1;parameter2:value2 For Eg: HRM:ECOFROST V3;Set_T:5;Set_H:92',
            deviceIdLabel: 'List of ecofrost devices',
            deviceIds:
              'Comma Seperated Serial Numbers Eg: E011901R003,E011901R002,E011912A003',
            expressionLabel: 'Ecofrost evaluation expression'
          };
        } else {
          return {
            commandLabel: '',
            command: '',
            deviceLabel: '',
            deviceIds: '',
            expressionLabel: ''
          };
        }
      } else {
        return {
          commandLabel: '',
          command: '',
          deviceLabel: '',
          deviceIds: '',
          expressionLabel: ''
        };
      }
    },
    user() {
      return this.$store.state.user.user;
    }
  },
  methods: {
    appendMeasurement(payload) {
      this.evaluationExpression = `${this.evaluationExpression}machine['${payload}']`;
    },
    appendNumberAndOperator(payload) {
      this.evaluationExpression = `${this.evaluationExpression}${payload}`;
    },
    async createEventBased() {
      this.loading = true;
      this.$store.commit('setError', false);
      try {
        if (this.$refs.form.validate()) {
          const response = await schedulerApi.post('/mqtt/event', {
            evaluate_expression: this.evaluationExpression,
            execution_command: this.command,
            created_by: this.user.email,
            machine_type: this.deviceType,
            machine_id_array: this.listOfDeviceIds
              .split(',')
              .map(machine_id => machine_id.trim().replace('\n', ''))
          });
          if (Array.isArray(response.data) && response.data.length > 0) {
            this.items = response.data.map(item => {
              return {
                ...item,
                updated_at: moment(item.updated_at).format('DD/MM/YY HH:mm:ss')
              };
            });
          } else {
            this.items = [];
          }
        }
      } catch (error) {
        if (error.isAxiosError) {
          this.$store.commit('setError', error.response.data.error.message);
        } else {
          this.$store.commit('setError', error);
        }
      } finally {
        this.loading = false;
        this.$refs.form.reset();
      }
    },
    async deleteRecord(payload) {
      try {
        const response = await schedulerApi.delete('/mqtt/event', {
          params: {
            event_id: payload.id
          }
        });
        if (Array.isArray(response.data) && response.data.length > 0) {
          this.items = response.data.map(item => {
            return {
              ...item,
              updated_at: moment(item.updated_at).format('DD/MM/YY HH:mm:ss')
            };
          });
        } else {
          this.items = [];
        }
      } catch (error) {
        if (error.isAxiosError) {
          this.$store.commit('setError', error.response.data.error.message);
        } else {
          this.$store.commit('setError', error);
        }
      }
    },
    async refreshResult() {
      this.$store.commit('setError', false);
      try {
        const response = await schedulerApi.get('/mqtt/event');
        if (Array.isArray(response.data) && response.data.length > 0) {
          this.items = response.data.map(item => {
            return {
              ...item,
              updated_at: moment(item.updated_at).format('DD/MM/YY HH:mm:ss')
            };
          });
        } else {
          this.items = [];
        }
      } catch (error) {
        if (error.isAxiosError) {
          this.$store.commit('setError', error.response.data.error.message);
        } else {
          this.$store.commit('setError', error);
        }
      }
    }
  },
  async created() {
    await this.refreshResult();
  }
};
</script>

<style></style>
